import { isThirdPartyLogistics } from "@redotech/account-common/team-type";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  globalSearchCategory,
  GlobalSearchCategory,
} from "@redotech/redo-views-common/global-search/global-search-category";
import {
  CommandPaletteFilter,
  CommandPaletteProvider,
} from "@redotech/redo-web/command-palette/contexts/command-palette-context";
import { memo, useCallback, useMemo } from "react";
import { SearchResultsLanguageSync } from "../side-navigation/search-results-language-sync";
import { useSearchHistory } from "../side-navigation/use-search-history";
import { useSearchItems } from "../side-navigation/use-search-items";

interface GlobalSearchProps {
  children: React.ReactNode;
}

export const GlobalSearch = memo(function GlobalSearch({
  children,
}: GlobalSearchProps) {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const onlyAssignedFulfillments = isThirdPartyLogistics(team);
  const { searchHistory, saveToSearchHistory } = useSearchHistory();
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const askRedoPanel = useAskRedoSlidePanel();
  const onSearch = useSearchItems(merchantRpcClient);

  const onAskAI = useCallback(
    (searchValue: string) => {
      askRedoPanel.open({
        kind: "prefill",
        input: searchValue,
        autoSubmit: true,
      });
    },
    [askRedoPanel],
  );

  const onAction = useCallback(
    (search: string) => {
      saveToSearchHistory(search);
    },
    [saveToSearchHistory],
  );

  const filters: CommandPaletteFilter[] = useMemo(() => {
    const labels: Record<GlobalSearchCategory, string> = {
      conversations: t("Conversations", {
        context: "navigation category for customer support conversations",
      }),
      orders: t("Orders", {
        context: "navigation category for merchant sales orders",
      }),
      shipments: t("Fulfillments", {
        context:
          "search category for merchant orders being picked, packed, and shipped from a warehouse, not abstract completion",
      }),
      returns: t("Returns", {
        context: "navigation category for customer product returns",
      }),
      withdrawals: t("Withdrawals", {
        context:
          "search category for customer statutory withdrawal-from-purchase requests, equivalent to the legal right of withdrawal, not cash or inventory withdrawals",
      }),
      customers: t("Customers", {
        context: "navigation category for merchant customer records",
      }),
      pages: t("Pages", {
        context: "navigation category for merchant app pages",
      }),
      documentation: t("Help Articles", {
        context: "navigation category for product help documentation",
      }),
    };
    return globalSearchCategory
      .filter(
        (category) => !onlyAssignedFulfillments || category === "shipments",
      )
      .map(
        (category): CommandPaletteFilter => ({
          id: category,
          label: labels[category],
          enabled: false,
          visible: true,
        }),
      );
  }, [onlyAssignedFulfillments, t]);

  return (
    <CommandPaletteProvider
      filters={filters}
      initialResults={searchHistory}
      onAction={onAction}
      onAskAI={onAskAI}
      onSearch={onSearch}
    >
      <SearchResultsLanguageSync />
      {children}
    </CommandPaletteProvider>
  );
});
